import { useCallback, useEffect, useState } from "react";
import type { Benutzer } from "../shared/types.js";
import { abmelden, istEingerichtet, ladeProfil } from "./api.js";
import { StammdatenProvider } from "./StammdatenContext.js";
import { Anmeldung } from "./pages/Login.js";
import { Dashboard } from "./pages/Dashboard.js";
import { EinladungEinloesen } from "./pages/EinladungEinloesen.js";
import { Ersteinrichtung } from "./pages/Ersteinrichtung.js";
import { Kunden } from "./pages/Kunden.js";
import { Angebote, legeUebernahmeAb } from "./pages/Angebote.js";
import { Preisfindung } from "./pages/Preisfindung.js";
import { Schnellkalkulation } from "./pages/Schnellkalkulation.js";
import { Stammdaten } from "./pages/Stammdaten.js";
import { PasswortAendern } from "./pages/PasswortAendern.js";

type Seite =
  | "dashboard"
  | "angebote"
  | "schnell"
  | "preisfindung"
  | "kunden"
  | "stammdaten"
  | "passwort";

const REITER: { id: Seite; titel: string; nurAdmin?: boolean }[] = [
  { id: "dashboard", titel: "Marktdaten" },
  // Angebote zuerst: dort liegt die Arbeit. Die Kalkulationsmaske steckt
  // seit dem Umbau darin, neben der Liste.
  { id: "angebote", titel: "Angebote" },
  { id: "schnell", titel: "Schnellkalkulation" },
  { id: "preisfindung", titel: "Preisfindung" },
  { id: "kunden", titel: "Kunden" },
  { id: "stammdaten", titel: "Stammdaten", nurAdmin: true },
  { id: "passwort", titel: "Passwort" },
];

/**
 * Der Einladungstoken steht im Fragment: der Browser überträgt es nie an
 * den Server, es taucht also in keinem Zugriffsprotokoll auf. Aus der
 * Adresszeile und dem Verlaufseintrag wird es sofort entfernt.
 */
function holeEinladungsToken(): string | null {
  if (location.pathname !== "/einladung") return null;
  const token = location.hash.slice(1);
  if (!token) return null;
  history.replaceState(null, "", "/einladung");
  return token;
}

/**
 * Einmalig auf Modulebene, NICHT im useState-Initialisierer: React ruft
 * Initialisierer unter StrictMode zweimal auf, und beim zweiten Mal wäre
 * das Fragment bereits entfernt — der Token ginge in der Entwicklung
 * verloren.
 */
const EINLADUNGS_TOKEN = holeEinladungsToken();

export function App() {
  // Vor allem anderen: eine Einladung braucht kein Profil und keine Session.
  const [einladung] = useState(EINLADUNGS_TOKEN);
  const [benutzer, setBenutzer] = useState<Benutzer | null>(null);
  const [eingerichtet, setEingerichtet] = useState<boolean | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [seite, setSeite] = useState<Seite>("dashboard");

  const laden = useCallback(async () => {
    if (einladung) return;
    try {
      const b = await ladeProfil();
      setBenutzer(b);
      setEingerichtet(true);
      if (b.must_change_password) setSeite("passwort");
    } catch {
      setBenutzer(null);
      // Nicht angemeldet: klären, ob es überhaupt schon einen Benutzer gibt.
      setEingerichtet(await istEingerichtet().catch(() => true));
    } finally {
      setLaedt(false);
    }
  }, [einladung]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const abmeldung = useCallback(async () => {
    await abmelden().catch(() => {});
    setBenutzer(null);
  }, []);

  if (einladung) return <EinladungEinloesen token={einladung} />;

  if (laedt)
    return (
      <div className="anmelde-seite">
        <p style={{ color: "var(--muted)" }}>Wird geladen …</p>
      </div>
    );

  if (!benutzer && eingerichtet === false)
    return (
      <Ersteinrichtung
        beiFertig={() => {
          setLaedt(true);
          void laden();
        }}
      />
    );

  if (!benutzer)
    return (
      <Anmeldung
        beiAnmeldung={(b) => {
          setBenutzer(b);
          setSeite(b.must_change_password ? "passwort" : "angebote");
        }}
      />
    );

  // Erstpasswort erzwingen, bevor irgendetwas anderes geht.
  if (benutzer.must_change_password && seite !== "passwort") setSeite("passwort");

  /*
   * Breite Seiten: Angebote und Preisfindung tragen eine Tabelle NEBEN einer
   * Seitenspalte. Bei 1180 px bleiben davon 675 px, was zwar reicht, sich
   * aber gestaucht anfuehlt.
   *
   * Wichtig: Die Klasse geht auch an Kopfzeile und Reiterzeile. Sonst laeuft
   * der Inhalt bis 1640 px, waehrend beide bei 1180 stehenbleiben — gemessen
   * 130…1310 gegen 0…1440, also sichtbar nicht mehr buendig.
   */
  const breit = seite === "angebote" || seite === "preisfindung";

  return (
    <StammdatenProvider>
      {/* data-pruef: Ankerpunkte fuer den Geraete-Pruefstand (pruefungen/). */}
      <header className="kopf" data-pruef="kopfzeile">
        <div className={`kopf-innen${breit ? " breit" : ""}`} data-pruef="kopfzeile-inhalt">
          <img src="/logo-engelke.png" alt="Gebr. Engelke — Große Mühle seit 1714" />
          <span className="kopf-titel">Angebotskalkulation</span>
          <div className="kopf-rechts">
            <span>
              {benutzer.name}
              {benutzer.role === "admin" && " · Administrator"}
            </span>
            <button className="knopf sekundaer" onClick={abmeldung}>
              Abmelden
            </button>
          </div>
        </div>
      </header>

      {!benutzer.must_change_password && (
        <nav className="navi">
          <div className={`navi-innen${breit ? " breit" : ""}`}>
            {REITER.filter((r) => !r.nurAdmin || benutzer.role === "admin").map((r) => (
              <button
                key={r.id}
                aria-current={seite === r.id ? "page" : undefined}
                onClick={() => setSeite(r.id)}
              >
                {r.titel}
              </button>
            ))}
          </div>
        </nav>
      )}

            <main className={breit ? "breit" : undefined}>
        {seite === "dashboard" && <Dashboard istAdmin={benutzer.role === "admin"} />}
        {seite === "angebote" && <Angebote />}
        {seite === "schnell" && (
          <Schnellkalkulation
            beiUebernahme={(p) => {
              legeUebernahmeAb(p);
              setSeite("angebote");
            }}
          />
        )}
        {seite === "preisfindung" && <Preisfindung istAdmin={benutzer.role === "admin"} />}
        {seite === "kunden" && <Kunden />}
        {seite === "stammdaten" && benutzer.role === "admin" && (
          <Stammdaten eigeneId={benutzer.id} />
        )}
        {seite === "passwort" && (
          <PasswortAendern
            erzwungen={benutzer.must_change_password}
            beiErfolg={() => {
              setBenutzer({ ...benutzer, must_change_password: false });
              setSeite("dashboard");
            }}
          />
        )}
      </main>

      <footer className="fuss">
        Gebr. Engelke Große Mühle Hasede-Hildesheim · interne Anwendung
      </footer>
    </StammdatenProvider>
  );
}
